import { Avatar, AvatarFallback } from '@/components/UI/Avatar';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/UI/Dropdown';
import { Label } from '@/components/UI/Label';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger
} from '@/components/UI/NavigationMenu';
import NotificationsDropdown from '@/components/UI/NotificationsDropdown';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/UI/Sheet';
import { Skeleton } from '@/components/UI/Skeleton/Skeleton';
import { Switch } from '@/components/UI/Switch';
import { Typography } from '@/components/UI/Typography';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { useCoiPermissions } from '@/features/coi/hooks/useCoiPermissions';
import {
  PendingApprovalsBadge,
  useHasAnySupplierUpdateApprovePermission,
  usePendingApprovalsList
} from '@/features/erp-sync';
import {
  OrganisationSwitcher,
  ReturnToMyCompanyMenuItem
} from '@/features/user-management';
import { useMenuNamingToggle } from '@/hooks/useMenuNamingToggle';
import PlaygroundDemoDataActions from '@/pages/NewUI/PlaygroundDemoDataActions';
import PlaygroundLiveDemoBadge from '@/pages/NewUI/PlaygroundLiveDemoBadge';
import PlaygroundModeMenuToggle from '@/pages/NewUI/PlaygroundModeMenuToggle';
import useAddNewCompany from '@/pages/NewUI/useAddNewCompany';
import { cn } from '@/util/cn';
import useCompanyProfileImage from '@/util/hooks/queries/useCompanyProfileImage';
import useUserProfile from '@/util/hooks/queries/useUserProfile';
import { MyCompanyPaths } from '@/util/routes/MyOffice';
import {
  BarChart3,
  Briefcase,
  Building2,
  ClipboardList,
  Cog,
  Database,
  GitBranch,
  LogOut,
  Menu,
  MessageSquare,
  Network,
  Plus,
  Settings,
  Shield,
  ShieldCheck,
  User,
  Users
} from 'lucide-react';
import { FC, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import PermissionClassification from '../../util/enums/PermissionClassification';
import useBaseCompanyInfo from '../../util/hooks/queries/useBaseCompanyInfo';
import useFeatureFlags from '../../util/hooks/queries/useFeatureFlags';
import useAllowedPermission from '../../util/hooks/useAllowedPermission';
import useAppLogOut from '../../util/hooks/useLogOutOfApp';
import { OnboardingSectionPaths } from './SupplierOnboardingRequest/Stepper/sections/onboardingSectionPaths';

const ERFX_ENDPOINT = import.meta.env.VITE_ERFX_ENDPOINT;

// Mobile Navigation Component
const MobileNavigation = ({
  setIsMobileMenuOpen,
  COMPANY_LINKS,
  OFFICE_LINKS,
  SETTINGS_LINKS,
  menuNaming
}: {
  setIsMobileMenuOpen: (open: boolean) => void;
  COMPANY_LINKS: any[];
  OFFICE_LINKS: any[];
  SETTINGS_LINKS: any[];
  menuNaming: ReturnType<typeof useMenuNamingToggle>;
}) => (
  <div className="space-y-5">
    {/* Dashboard Link */}
    <div className="border-b border-slate-200 pb-3">
      <Link
        to="/"
        className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50"
        onClick={() => setIsMobileMenuOpen(false)}
        prefetch="render"
      >
        <BarChart3 className="h-4 w-4 text-slate-500" />
        Dashboard
      </Link>
    </div>

    {/* Marketplaces Link */}
    <div className="border-b border-slate-200 pb-3">
      <Link
        prefetch="render"
        to="/virtual-marketplace"
        className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50"
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <Building2 className="h-4 w-4 text-slate-500" />
        Marketplaces
      </Link>
    </div>

    {/* Network Section (formerly "Company", #6096 AC9) — label toggled via menuNaming */}
    <div className="space-y-1">
      <Typography
        variant="h6"
        className="px-4 pb-1 text-xs font-medium tracking-wider text-slate-400 uppercase"
      >
        {menuNaming.labels.network}
      </Typography>
      {COMPANY_LINKS.map((link) => {
        const IconComponent = link.icon;
        return (
          <Link
            prefetch="render"
            key={link.label}
            to={link.href}
            className={cn(
              'flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-slate-50',
              (!link.permission || link?.disabled) && 'pointer-events-none text-slate-400'
            )}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <IconComponent className="h-4 w-4 text-slate-500" />
            <span className="flex items-center gap-2">
              {link.label}
              {link?.disabled && (
                <Badge variant="outlined" className="text-[10px]">
                  Coming Soon
                </Badge>
              )}
            </span>
          </Link>
        );
      })}
    </div>

    {/* Work Section (formerly "Office", #6096 AC7) — label toggled via menuNaming */}
    <div className="space-y-1">
      <Typography
        variant="h6"
        className="px-4 pb-1 text-xs font-medium tracking-wider text-slate-400 uppercase"
      >
        {menuNaming.labels.work}
      </Typography>
      {OFFICE_LINKS.map((link) => {
        const IconComponent = link.icon;
        return (
          <Link
            prefetch="render"
            key={link.label}
            to={link.href}
            className={cn(
              'flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-slate-50',
              (!link.permission || link?.disabled) && 'pointer-events-none text-slate-400'
            )}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <IconComponent className="h-4 w-4 text-slate-500" />
            <span className="flex items-center gap-2">
              {link.label}
              {!!link?.badgeCount && (
                <Badge color="danger" size="sm">
                  {link.badgeCount > 99 ? '99+' : link.badgeCount}
                </Badge>
              )}
              {link?.disabled && (
                <Badge variant="outlined" className="text-[10px]">
                  Coming Soon
                </Badge>
              )}
            </span>
          </Link>
        );
      })}
    </div>

    {/* Settings Section */}
    <div className="space-y-1">
      <Typography
        variant="h6"
        className="px-4 pb-1 text-xs font-medium tracking-wider text-slate-400 uppercase"
      >
        Settings
      </Typography>
      {SETTINGS_LINKS.map((link) => {
        const IconComponent = link.icon;
        return (
          <Link
            prefetch="render"
            key={link.label}
            to={link.href}
            className={cn(
              'flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-slate-50',
              (!link.permission || link?.disabled) && 'pointer-events-none text-slate-400'
            )}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <IconComponent className="h-4 w-4 text-slate-500" />
            <span className="flex items-center gap-2">
              {link.label}
              {link?.disabled && (
                <Badge variant="outlined" className="text-[10px]">
                  Coming Soon
                </Badge>
              )}
            </span>
          </Link>
        );
      })}
    </div>

    {/* Help & Support */}
    <div className="border-t border-slate-200 pt-3">
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://b1sa.helpscoutdocs.com"
        className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50"
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <Shield className="h-4 w-4 text-slate-500" />
        Help & Support
      </a>
    </div>
  </div>
);

const NavigationHeader = () => {
  const { handleHasPermissionCheckFunction: hasAllowedPermission, isLoading } =
    useAllowedPermission();
  const { isSupportMode } = useSupportMode();
  const { data: currentCompany } = useBaseCompanyInfo();
  const { branchOnboarding } = useFeatureFlags();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuNaming = useMenuNamingToggle();
  const { hasAnyApprovePermission, isLoading: hasAnyApprovePermissionLoading } =
    useHasAnySupplierUpdateApprovePermission();
  // #7751. hasAnyCoiPermission is already false while the permission list is loading
  // (useCoiPermissions fails closed), so the COI entry stays hidden until it resolves — AC9's "no
  // navigation entry is shown" — with no separate isLoading guard needed at this call site.
  const { hasAnyCoiPermission, landingPath: coiLandingPath } = useCoiPermissions();
  // Only polled once the user could actually see the count (avoids an unnecessary
  // request — and a flash of "0" — for users with no SupplierUpdateApprove* permission).
  const { data: pendingApprovalRows } = usePendingApprovalsList({
    enabled: !hasAnyApprovePermissionLoading && hasAnyApprovePermission
  });
  const pendingApprovalsCount = hasAnyApprovePermission
    ? (pendingApprovalRows?.length ?? 0)
    : 0;
  const TENDERS_LINKS = [
    {
      label: 'Opportunities',
      href: `${ERFX_ENDPOINT}/bidder-rfq-listings`,
      permission: true,
      disabled: false,
      icon: ClipboardList
    },
    {
      label: 'Initiator Workstation',
      href: `${ERFX_ENDPOINT}/initiator-workstation`,
      permission: true,
      disabled: false,
      icon: Briefcase
    },
    {
      label: 'Bidding Workstation',
      href: `${ERFX_ENDPOINT}/bidder-workstation`,
      permission: true,
      disabled: false,
      icon: Briefcase
    }
  ];

  // #6096 AC9-10 — "Company" -> "Network": Network & Connections first (highest-traffic
  // per 90-day PostHog click data), Company Profile second, Business Tools last.
  const COMPANY_LINKS = [
    {
      label: 'Network & Connections',
      href: '/network/suppliers',
      permission: true,
      disabled: false,
      icon: Network
    },
    {
      label: 'Company Profile',
      href: '/view-profile/' + currentCompany?.companyId,
      permission: !isLoading
        ? hasAllowedPermission(PermissionClassification.ViewMyProfile)
        : false,
      disabled: false,
      icon: Building2
    }
  ];

  // #6096 AC7-8 — "Office" -> "Work": Onboarding Dashboard first, then the new Supplier
  // Updates item (with a pending-count badge), then a divider before the rest, with the
  // still-disabled placeholders last.
  const OFFICE_LINKS = [
    {
      label: 'Onboarding Dashboard',
      href: `${OnboardingSectionPaths.SupplierOnboardingDashboardRequestsSent}`,
      permission: true,
      icon: ClipboardList
    },
    {
      label: 'Supplier Updates',
      href: '/pending-approvals',
      permission: !hasAnyApprovePermissionLoading && hasAnyApprovePermission,
      disabled: false,
      icon: ShieldCheck,
      badgeCount: pendingApprovalsCount
    },
    {
      label: 'Agent Work Centre',
      href: '/agent-workcentre',
      permission: hasAllowedPermission(
        PermissionClassification.CanAccessWorkcentreAsAgent
      ),
      disabled: false,
      icon: Briefcase
    },
    {
      label: 'Data Management',
      href: '/datamanagment-partners',
      permission: hasAllowedPermission(
        PermissionClassification.CanAccessWorkcentreAsAgent
      ),
      disabled: false,
      icon: Database
    },
    {
      label: 'Branch Requests',
      href: '/branch-requests',
      permission:
        branchOnboarding &&
        (isLoading
          ? false
          : hasAllowedPermission(PermissionClassification.ManageBranchApprovals)),
      disabled: false,
      icon: GitBranch
    },
    // COI (Conflict of Interest) management shell. #7751 AC1: the entry is ABSENT for a user
    // holding none of the eight COI permissions, not greyed out.
    //
    // THIS DIVERGES FROM EVERY OTHER ENTRY HERE, deliberately. The shared renderer marks a denied
    // link `pointer-events-none text-slate-400` and leaves it in the DOM, so a denied link is
    // visible-but-dead. #7751 forbids that for COI specifically ("hide, do not grey out" — a locked
    // tab tells the reader that something exists they are excluded from, which is the disclosure the
    // story exists to close). Spreading the entry in conditionally keeps that difference local to
    // COI instead of changing how every other permission-gated entry behaves.
    //
    // The href is the FIRST PERMITTED section, not a hard-coded /check-runs: AC2 requires a user
    // holding only Cases to land on Cases. `landingPath` is null exactly when the entry is hidden,
    // so the `hasAnyCoiPermission` guard already covers it — the `?? ''` is unreachable and present
    // only to satisfy the string type.
    ...(hasAnyCoiPermission
      ? [
          {
            label: 'COI Management',
            href: coiLandingPath ?? '',
            permission: true,
            disabled: false,
            icon: Shield
          }
        ]
      : [])
  ];

  const SETTINGS_LINKS = [
    {
      label: 'User Management',
      href: MyCompanyPaths.UserManagement.users,
      permission: !isLoading
        ? hasAllowedPermission(PermissionClassification.ManageUsers)
        : false,
      disabled: false,
      icon: Users
    },
    {
      label: 'Platform Admin',
      href: '/adminDashboard',
      permission: !isLoading
        ? hasAllowedPermission(PermissionClassification.SupportCompanyContextSwitch)
        : false,
      disabled: false,
      icon: Settings
    },
    {
      label: 'Onboarding Configuration',
      href: '/myoffice/configuration',
      permission: !isLoading
        ? hasAllowedPermission(PermissionClassification.CorporateOnboarding)
        : false,
      disabled: false,
      icon: Settings
    },
    {
      label: 'Company Settings',
      href: '/edit-company-profile',
      permission: !isLoading
        ? hasAllowedPermission(PermissionClassification.EditMyProfile)
        : false,
      disabled: false,
      icon: Cog
    },
    {
      // Kept: /accountPreferences is a real route (AppPages.tsx) rendering UserProfilePage.
      // Still flagged disabled pending a product decision to switch it on.
      label: 'My Profile',
      href: '/accountPreferences',
      permission: true,
      disabled: true,
      icon: User
    }
  ];

  const { pathname } = useLocation();

  // Active nav state — match current pathname against each section's link hrefs
  const isSectionActive = (links: { href: string }[]) =>
    links.some(
      (link) =>
        link.href && !link.href.startsWith('http') && pathname.startsWith(link.href)
    );

  const isDashboardActive = pathname === '/';
  const isMarketplacesActive = pathname.startsWith('/virtual-marketplace');
  const isCompanyActive = isSectionActive(COMPANY_LINKS);
  const isOfficeActive = isSectionActive(OFFICE_LINKS);
  const isSettingsActive = isSectionActive(SETTINGS_LINKS);

  return (
    <nav
      className={cn(
        'sticky top-0 z-50 border-b border-white/10',
        isSupportMode ? 'support-mode-nav' : 'bg-primary'
      )}
      data-support-mode={isSupportMode ? 'true' : undefined}
      aria-label={isSupportMode ? 'Support mode navigation' : undefined}
    >
      <div className="mx-auto max-w-[105rem] px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          {/* Brand & Tenant */}
          <div className="flex items-center gap-4">
            <Link to="/" prefetch="render" className="flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100"
                height="23"
                viewBox="0 0 172.786 28.054"
                className="h-6 w-auto"
              >
                <path
                  d="M40.123,7.014V28.054h9.092V0H33.559V7.014ZM13.941,20.96c2.06,0,3.111-.842,3.111-2.043v-.081c0-1.2-1.01-2.043-3.071-2.043H9.213V20.96Zm-.606-9.941c2.019,0,2.989-.678,2.989-1.922V9.017c0-1.24-1.009-1.922-3.029-1.922H9.213v3.926ZM0,0H15.838c3.921,0,6.384.881,8.042,2.525a5.973,5.973,0,0,1,1.737,4.367v.081c0,3.325-2.02,5.371-5.05,6.412,3.635,1.042,5.98,2.966,5.98,6.814v.079c0,4.49-3.717,7.776-10.868,7.776H0ZM172.786,28.054,158.7,14.083v-.112L172.785,0H161.357L150.751,10.52h-2.536V0h-9.09V28.054h9.091V17.534h2.536l10.606,10.52ZM77.408,21.041V0h-9.09V28.054H89.53V21.041ZM104.683,0H95.591V28.054h9.091Zm25.352,16.131V0H113.773Zm0,11.923H113.773V11.923Z"
                  fill={isSupportMode ? 'currentColor' : '#ffffff'}
                  className={isSupportMode ? 'text-white' : ''}
                />
              </svg>
            </Link>
            <PlaygroundLiveDemoBadge />
            {isSupportMode && (
              <Badge
                variant="outlined"
                size="sm"
                className="hidden border-amber-500/60 bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-200 md:inline-flex"
                title="You are viewing the platform as a support administrator"
              >
                Support Mode
              </Badge>
            )}
            {currentCompany?.companyRegisteredName && (
              <div className="hidden items-center gap-2 border-l border-white/20 pl-4 md:flex">
                <span
                  className="max-w-[200px] truncate text-sm font-medium tracking-tight text-white/80 lg:max-w-[300px]"
                  title={currentCompany.companyRegisteredName}
                >
                  {isSupportMode ? (
                    <>
                      <span className="text-white/60">Viewing as: </span>
                      <span className="font-semibold text-white/95">
                        {currentCompany.companyRegisteredName}
                      </span>
                    </>
                  ) : (
                    currentCompany.companyRegisteredName
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden flex-1 justify-center lg:flex lg:items-center lg:space-x-8">
            <NavigationMenu viewport={false} className="w-full max-w-4xl">
              <NavigationMenuList className="space-x-2">
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/"
                      prefetch="render"
                      className={cn(
                        'rounded-md px-4 py-2 text-[13px] font-medium transition-all duration-150 hover:bg-white/15 hover:text-white',
                        isDashboardActive ? 'bg-white/15 text-white' : 'text-white/80'
                      )}
                    >
                      Dashboard
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/virtual-marketplace"
                      prefetch="render"
                      className={cn(
                        'rounded-md px-4 py-2 text-[13px] font-medium transition-all duration-150 hover:bg-white/15 hover:text-white',
                        isMarketplacesActive ? 'bg-white/15 text-white' : 'text-white/80'
                      )}
                    >
                      Marketplaces
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={cn(
                      'text-[13px] font-medium transition-colors duration-150 hover:text-white',
                      isCompanyActive
                        ? 'bg-white/15 text-white'
                        : 'bg-transparent text-white/80'
                    )}
                  >
                    {menuNaming.labels.network}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="md:w-80">
                    {COMPANY_LINKS.map((link, index) => {
                      const IconComponent = link.icon;
                      const isActive = link.href && pathname.startsWith(link.href);
                      return (
                        <NavigationMenuLink key={link.label} asChild>
                          <>
                            {index > 0 && (
                              <DropdownMenuSeparator className="-mx-4 my-1" />
                            )}
                            <Link
                              prefetch="render"
                              className={cn(
                                'flex items-center gap-3 px-4 py-3 text-sm font-normal tracking-tight transition-colors duration-200 hover:bg-gray-50 hover:text-gray-900',
                                (!link.permission || link?.disabled) &&
                                  'text-muted-foreground pointer-events-none opacity-50',
                                isActive && 'bg-primary/5 text-primary font-medium'
                              )}
                              to={link.href}
                            >
                              <IconComponent
                                className={cn(
                                  'h-4 w-4 stroke-1',
                                  (!link.permission || link?.disabled) && 'opacity-50',
                                  isActive && 'text-primary'
                                )}
                              />
                              <span className="flex items-center gap-2">
                                {link.label}
                                {link?.disabled && (
                                  <Badge
                                    variant="outlined"
                                    className="border-gray-200 bg-gray-100 text-xs text-gray-500"
                                  >
                                    Coming Soon
                                  </Badge>
                                )}
                              </span>
                            </Link>
                          </>
                        </NavigationMenuLink>
                      );
                    })}
                  </NavigationMenuContent>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={cn(
                      'text-[13px] font-medium transition-colors duration-150 hover:text-white',
                      isOfficeActive
                        ? 'bg-white/15 text-white'
                        : 'bg-transparent text-white/80'
                    )}
                  >
                    {menuNaming.labels.work}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="md:w-80">
                    {OFFICE_LINKS.map((link, index) => {
                      const IconComponent = link.icon;
                      const isActive = link.href && pathname.startsWith(link.href);
                      return (
                        <NavigationMenuLink key={link.label} asChild>
                          <>
                            {index > 0 && (
                              <DropdownMenuSeparator className="-mx-4 my-1" />
                            )}
                            <Link
                              prefetch="render"
                              className={cn(
                                'flex items-center gap-3 px-4 py-3 text-sm font-normal tracking-tight transition-colors duration-200 hover:bg-gray-50 hover:text-gray-900',
                                (!link.permission || link?.disabled) &&
                                  'text-muted-foreground pointer-events-none opacity-50',
                                isActive && 'bg-primary/5 text-primary font-medium'
                              )}
                              to={link.href}
                            >
                              <IconComponent
                                className={cn(
                                  'h-4 w-4 stroke-1',
                                  (!link.permission || link?.disabled) && 'opacity-50',
                                  isActive && 'text-primary'
                                )}
                              />
                              <span className="flex items-center gap-2">
                                {link.label}
                                {!!link?.badgeCount && (
                                  <Badge color="danger" size="sm">
                                    {link.badgeCount > 99 ? '99+' : link.badgeCount}
                                  </Badge>
                                )}
                                {link?.disabled && (
                                  <Badge
                                    variant="outlined"
                                    className="border-gray-200 bg-gray-100 text-xs text-gray-500"
                                  >
                                    Coming Soon
                                  </Badge>
                                )}
                              </span>
                            </Link>
                          </>
                        </NavigationMenuLink>
                      );
                    })}
                  </NavigationMenuContent>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={cn(
                      'text-[13px] font-medium transition-colors duration-150 hover:text-white',
                      isSettingsActive
                        ? 'bg-white/15 text-white'
                        : 'bg-transparent text-white/80'
                    )}
                  >
                    Settings
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="md:w-80">
                    {SETTINGS_LINKS.map((link, index) => {
                      const IconComponent = link.icon;
                      const isActive = link.href && pathname.startsWith(link.href);
                      return (
                        <NavigationMenuLink key={link.label} asChild>
                          <>
                            {index > 0 && (
                              <DropdownMenuSeparator className="-mx-4 my-1" />
                            )}
                            <Link
                              prefetch="render"
                              className={cn(
                                'flex items-center gap-3 px-4 py-3 text-sm font-normal tracking-tight transition-colors duration-200 hover:bg-gray-50 hover:text-gray-900',
                                (!link.permission || link?.disabled) &&
                                  'text-muted-foreground pointer-events-none opacity-50',
                                isActive && 'bg-primary/5 text-primary font-medium'
                              )}
                              to={link.href}
                            >
                              <IconComponent
                                className={cn(
                                  'h-4 w-4 stroke-1',
                                  (!link.permission || link?.disabled) && 'opacity-50',
                                  isActive && 'text-primary'
                                )}
                              />
                              <span className="flex items-center gap-2">
                                {link.label}
                                {link?.disabled && (
                                  <Badge
                                    variant="outlined"
                                    className="border-gray-200 bg-gray-100 text-xs text-gray-500"
                                  >
                                    Coming Soon
                                  </Badge>
                                )}
                              </span>
                            </Link>
                          </>
                        </NavigationMenuLink>
                      );
                    })}
                  </NavigationMenuContent>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-[13px] font-medium text-white/80 transition-colors duration-150 hover:text-white">
                    Tenders
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="md:w-64">
                    {TENDERS_LINKS.map((link, index) => {
                      const IconComponent = link.icon;
                      return (
                        <NavigationMenuLink key={link.label} asChild>
                          <>
                            {index > 0 && (
                              <DropdownMenuSeparator className="-mx-4 my-1" />
                            )}
                            <a
                              className={cn(
                                'flex items-center gap-3 px-4 py-3 text-sm font-normal tracking-tight transition-colors duration-200 hover:bg-gray-50 hover:text-gray-900',
                                (!link.permission || link?.disabled) &&
                                  'text-muted-foreground pointer-events-none opacity-50'
                              )}
                              href={link.href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconComponent
                                className={cn(
                                  'h-4 w-4 stroke-1',
                                  (!link.permission || link?.disabled) && 'opacity-50'
                                )}
                              />
                              <span className="flex items-center gap-2">
                                {link.label}
                                {link?.disabled && (
                                  <Badge
                                    variant="outlined"
                                    className="border-gray-200 bg-gray-100 text-xs text-gray-500"
                                  >
                                    Coming Soon
                                  </Badge>
                                )}
                              </span>
                            </a>
                          </>
                        </NavigationMenuLink>
                      );
                    })}
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Right side - Approvals badge, Notifications and User Menu */}
          <div className="flex items-center gap-5">
            {/* Top-level Approvals badge (#6096 AC12) — deep-links to the Pending
                Approvals Inbox; renders nothing when not permitted or count is 0 */}
            <PendingApprovalsBadge />

            {/* Notifications - Hidden on mobile */}
            <div className="hidden sm:block">
              <NotificationsDropdown />
            </div>

            {/* User Menu */}
            <UserMenu />

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outlined"
                    size="sm"
                    className="border-white/20 p-2 text-white/80 hover:bg-white/10 hover:text-white"
                  >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className={cn(
                    'w-80 p-0',
                    isSupportMode &&
                      'support-mode-sheet border-l border-slate-600 bg-slate-900'
                  )}
                >
                  <SheetHeader
                    className={cn(
                      'border-b px-6 py-4',
                      isSupportMode ? 'border-slate-700' : 'border-slate-200'
                    )}
                  >
                    <SheetTitle className="text-left">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="100"
                        height="23"
                        viewBox="0 0 172.786 28.054"
                        className="h-6 w-auto"
                      >
                        <path
                          d="M40.123,7.014V28.054h9.092V0H33.559V7.014ZM13.941,20.96c2.06,0,3.111-.842,3.111-2.043v-.081c0-1.2-1.01-2.043-3.071-2.043H9.213V20.96Zm-.606-9.941c2.019,0,2.989-.678,2.989-1.922V9.017c0-1.24-1.009-1.922-3.029-1.922H9.213v3.926ZM0,0H15.838c3.921,0,6.384.881,8.042,2.525a5.973,5.973,0,0,1,1.737,4.367v.081c0,3.325-2.02,5.371-5.05,6.412,3.635,1.042,5.98,2.966,5.98,6.814v.079c0,4.49-3.717,7.776-10.868,7.776H0ZM172.786,28.054,158.7,14.083v-.112L172.785,0H161.357L150.751,10.52h-2.536V0h-9.09V28.054h9.091V17.534h2.536l10.606,10.52ZM77.408,21.041V0h-9.09V28.054H89.53V21.041ZM104.683,0H95.591V28.054h9.091Zm25.352,16.131V0H113.773Zm0,11.923H113.773V11.923Z"
                          fill="#4b69f9"
                        />
                      </svg>
                    </SheetTitle>
                  </SheetHeader>
                  <div className="py-6">
                    <MobileNavigation
                      setIsMobileMenuOpen={setIsMobileMenuOpen}
                      COMPANY_LINKS={COMPANY_LINKS}
                      OFFICE_LINKS={OFFICE_LINKS}
                      SETTINGS_LINKS={SETTINGS_LINKS}
                      menuNaming={menuNaming}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

const UserMenu: FC = () => {
  const { data: currentCompany } = useBaseCompanyInfo();
  const { data: profileImage } = useCompanyProfileImage(currentCompany?.companyId ?? 0);
  const navigate = useNavigate();
  const { data: user, isLoading: isLoadingUserProfile } = useUserProfile();
  const logoutOfApp = useAppLogOut();
  const { start: handleAddNewCompany, isStarting: isStartingNewCompany } =
    useAddNewCompany();
  const menuNaming = useMenuNamingToggle();

  if (isLoadingUserProfile) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg bg-white/10 px-3 py-1.5">
        <div className="hidden sm:block">
          <Skeleton className="mb-1 h-3.5 w-24 bg-white/20" />
          <Skeleton className="h-2.5 w-32 bg-white/15" />
        </div>
        <Skeleton className="size-8 rounded-full bg-white/25" />
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex max-w-60 cursor-pointer items-center gap-2.5 rounded-lg bg-white/10 px-3 py-1.5 transition-colors duration-150 hover:bg-white/[0.18] focus:outline-none"
        >
          <div className="hidden text-right sm:block">
            <span
              title={`${user?.name} ${user?.surname}`}
              className="line-clamp-1 max-w-44 text-[13px] leading-tight font-semibold text-white"
            >
              {user?.name} {user?.surname}
            </span>
            <p
              title={user?.email}
              className="mt-0.5 line-clamp-1 w-full max-w-44 text-[11px] leading-none text-white/70"
            >
              {user?.email}
            </p>
          </div>
          <Avatar className="size-8 rounded-full bg-white/25">
            <AvatarFallback className="size-8 rounded-full text-sm font-semibold text-white uppercase">
              {(() => {
                if (user?.name?.charAt(0)) {
                  return user?.name?.charAt(0);
                }
                if (user?.email) {
                  return user.email.charAt(0);
                }
                return 'U';
              })()}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-0">
        <OrganisationSwitcher
          currentName={currentCompany?.companyRegisteredName}
          profileImageUrl={profileImage?.url}
        />
        <DropdownMenuSeparator className="my-0" />
        <div className="py-1">
          <ReturnToMyCompanyMenuItem />
          <DropdownMenuItem
            onClick={() => navigate('/messaging')}
            className="gap-2.5 px-4 py-2 text-[13px] font-normal text-gray-700"
          >
            <MessageSquare className="h-4 w-4 text-gray-400" />
            Messages
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate('/edit-company-profile')}
            className="gap-2.5 px-4 py-2 text-[13px] font-normal text-gray-700"
          >
            <Cog className="h-4 w-4 text-gray-400" />
            Company Settings
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleAddNewCompany}
            disabled={isStartingNewCompany}
            className="gap-2.5 px-4 py-2 text-[13px] font-normal text-gray-700"
          >
            <Plus className="h-4 w-4 text-gray-400" />
            {isStartingNewCompany ? 'Starting...' : 'Add new company'}
          </DropdownMenuItem>
        </div>
        <DropdownMenuSeparator className="my-0" />
        {/* Menu labels toggle (#6096) — switch between the new (Network/Work) and previous
            (Company/Office) section names. Persisted per-device in origin-isolated
            localStorage. Rendered as a plain row (not a DropdownMenuItem) and with pointer
            events stopped so flipping the switch doesn't close the menu. */}
        <div
          className="flex items-start justify-between gap-3 px-4 py-2.5"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col">
            <Label
              htmlFor="menu-naming-toggle"
              className="text-[13px] font-medium text-gray-700"
            >
              Use updated menu names
            </Label>
            <span className="mt-0.5 text-[11px] text-gray-400">
              {menuNaming.useNewMenuNames
                ? 'Showing “Network” and “Work”.'
                : 'Showing the previous “Company” and “Office”.'}
            </span>
          </div>
          <Switch
            id="menu-naming-toggle"
            checked={menuNaming.useNewMenuNames}
            onCheckedChange={menuNaming.toggle}
            aria-label="Toggle between updated and previous menu names"
          />
        </div>
        {/* QA Playground Mode toggle (Feature #8075, PBI #8080) — renders nothing once the
            status check confirms the env gate is off (AC1). Note this menu item isn't what
            makes prod a no-op: PlaygroundModeBanner is mounted unconditionally in root.tsx
            and issues the same GET regardless of whether this menu ever opens — every prod
            session still makes that call, and it 404s by design (review, PR #2577). */}
        <PlaygroundModeMenuToggle />
        {/* QA Playground demo data (Feature #8595, US-5) - "Load Demo Data"/"Reset Demo
            Data" for client pitches. Renders nothing until Playground Mode is actually ON
            for this session (see PlaygroundDemoDataActions). */}
        <PlaygroundDemoDataActions />
        <DropdownMenuSeparator className="my-0" />
        <div className="py-1">
          <DropdownMenuItem
            onClick={() => logoutOfApp()}
            className="gap-2.5 px-4 py-2 text-[13px] font-normal text-gray-700"
          >
            <LogOut className="h-4 w-4 text-gray-400" />
            Logout
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NavigationHeader;
